import { createApi } from '@reduxjs/toolkit/query/react';
import { Driver } from '@typeRoots/common/drivers';
import { setAllDrivers, setDriver, updateDriver, deleteDriver } from '../slice/driverSlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';

export const driversApi = createApi({
  // Unique key for the API slice in Redux state
  reducerPath: 'driversApi',

  // Configure base query settings, including the base URL for all requests
  baseQuery: baseQueryWithProgress,

  // Define cache tag types for automatic cache invalidation
  tagTypes: ['Drivers', 'Driver'],

  // Define API endpoints (queries and mutations)
  endpoints: (builder) => ({
    getAllDrivers: builder.query<Driver[], number>({
      query: (id) => `/drivers/company/${id}`, //here we will pass compnay Id so that we get drivers for current company only.
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          const { data: drivers } = await queryFulfilled; //drivers response array
          dispatch(setAllDrivers(drivers)); //update all drivers in the redux store
        } catch (error) {
          console.log('Error fetching all drivers', error);
        }
      },
      // Tagging drivers to automatically refresh this cache when needed
      providesTags: ['Drivers'],
    }),

    getDriverById: builder.query<Driver, number>({
      query: (id) => `/drivers/${id}`,
      async onQueryStarted(queryFulfilled) {
        try {
          await queryFulfilled;
          //reducer functions if needed
        } catch (error) {
          console.log('Error fetching driver', error);
        }
      },
      // Tagging Driver to automatically refresh this cache when needed
      providesTags: ['Driver'],
    }),
    uploadDlImage: builder.mutation<Partial<Driver>, FormData>({
      query: (formData) => ({
        url: '/dl-extractor/data', // Image upload endpoint
        method: 'POST',
        body: formData,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // Optionally, trigger a refetch of vehicles or update Redux state if needed
        } catch (error) {
          console.log('Error uploading image', error);
        }
      },
    }),
    //create driver endpoint
    createDriver: builder.mutation<Driver, Partial<Driver>>({
      query: (newDriver) => ({
        url: '/drivers',
        method: 'POST',
        body: newDriver,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(driver, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setDriver(data));
        } catch (e) {
          console.log('Create Driver error: ', e);
        }
      },
    }),

    //update driver endpoint
    updateDriver: builder.mutation<Driver, { id: number; updateDriverData: Partial<Driver> }>({
      query: ({ id, updateDriverData }) => ({
        url: `/drivers/${id}`,
        method: 'PATCH',
        body: JSON.stringify(updateDriverData),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(updateDriver({ ...params.updateDriverData, id: params.id }));
        } catch (e) {
          console.log('Update Driver data error', e);
        }
      },
    }),

    // Delete a driver by ID
    deleteDriverById: builder.mutation<void, number>({
      query: (id) => ({
        url: `/drivers/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(deleteDriver(id)); //delete this driver from drivers array in store
        } catch (e) {
          console.log('Delete Driver error', e);
        }
      },
      // Invalidate the cache for the deleted driver and refetch the list
      invalidatesTags: ['Driver'],
    }),
  }),
});

// Export generated hooks for each endpoint to use them in components
export const {
  useGetDriverByIdQuery,
  useGetAllDriversQuery,
  useLazyGetAllDriversQuery,
  useLazyGetDriverByIdQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverByIdMutation,
  useUploadDlImageMutation,
} = driversApi;
